<template>
    <v-container>
        <v-layout>
            <loader id="loader" v-show="isLoading" />
        </v-layout>
        <v-layout>
            <h2 class="hideMobile">{{ txc.page_title }}</h2>
        </v-layout>
        <v-layout column>
            <div flat class="verified-container">
                <div class="left-side">
                    <h5 class="cardSectionTitle">{{ txc.card_title_account }}</h5>
                </div>
                <div class="right-side" v-if="showEmailVerify">
                    <div class="badge-container" :class="isEmailVerified ? 'success' : 'danger'">
                        <NsIcon 
                            :icon-name="isEmailVerified ? 'icon-verified' : 'icon-exclamation'" 
                            icon-size="icon-sm" 
                        />
                        {{isEmailVerified ? `${sharedStrings.email_verification.personal_info_email_verified}`
                            : `${sharedStrings.email_verification.personal_info_email_not_verified}`}}
                    </div>
                </div>
            </div>
            <v-card flat class="mb-2">
                <v-card-text>
                    <section>
                        <div class="field-label">
                            {{
                                showEmailVerify
                                    ? `${sharedStrings.accounts.email_address} / ${sharedStrings.accounts.username}`
                                    : `${sharedStrings.accounts.username}`
                            }}
                        </div>
                        <div class="field-value">{{ getUsername }}</div>
                        <div class="field-edit" v-if="showEmailVerify">
                            <!-- txc.edit_username is not available right now -->
                            <a href="#" id="editEmailDialogBtn" @click.stop="usernameDialog=true">
                                {{ sharedStrings.email_verification.email_verify_card_verify_change_button }}
                            </a>                        </div>
                        <edit-username-email-dialog v-if="usernameDialog" @closeDialog="handleCloseDialog" />
                    </section>
                    <section>
                        <div class="field-label">{{ sharedStrings.accounts.password }}</div>
                        <div class="field-value">*******</div>
                        <div class="field-edit">
                            <a 
                                href="#" 
                                data-testid="change_password" 
                                @click.stop="editPasswordDialog = true">
                                {{ txc.change_password }}
                            </a>
                        </div>
                        <edit-password-dialog
                            v-if="editPasswordDialog"
                            :fullscreen="fullScreenModals"
                            :email="account.email"
                            @closeDialog="editPasswordDialog = false"
                        />
                    </section>
                </v-card-text>
            </v-card>
            <v-card flat class="mb-2">
                <v-card-text>
                    <section>
                        <div class="field-label">{{ txc.profile_image_text }}</div>
                        <v-avatar size="75" @click.stop="profileDialog = true" class="clickable mb-2">
                            <v-img :src="profilePic" aspect-ratio="1"/>
                        </v-avatar>
                        <div class="field-edit">
                            <a href="#" @click.stop="openProfileDialog">{{ txc.edit_profile_image }}</a>
                        </div>
                        <ns-modal
                            v-model="profileDialog"
                            :fullscreen="fullScreenModals"
                            :title="txc.edit_profile_image"
                            @input="closeProfileDialog"
                        >
                            <!-- <div class="field-label">New Profile Image</div> -->
                            <!-- <v-text-field label="Select Image" @click='pickFile' v-model='imageName'
            prepend-icon='attach_file'></v-text-field>-->
                            <server-error v-if="!!dialogError" :error="dialogError" />
                            <v-dragdrop-small
                                @result="this.updateProfileImageData"
                                class="mb-5"
                                ref="imageInput"
                            />
                            <div id="loader" class="hidden">&nbsp;</div>
                            <template v-slot:actions>
                                <v-layout column>
                                    <v-flex xs12>
                                        <button
                                            flat
                                            block
                                            class="primary-hit mb-2 fluid"
                                            @click="uploadImage()"
                                            data-test-id="personalInfo-image-modal-save"
                                        >{{ sharedStrings.generic.save }}</button>
                                    </v-flex>
                                    <v-spacer/>
                                    <v-flex xs12>
                                        <button
                                            flat
                                            block
                                            outline
                                            class="secondary-hit fluid"
                                            @click="closeProfileDialog"
                                            data-test-id="personalInfo-image-modal-cancel"
                                        >{{ sharedStrings.generic.cancel }}</button>
                                    </v-flex>
                                </v-layout>
                            </template>
                        </ns-modal>
                    </section>
                </v-card-text>
            </v-card>
            <v-card flat class="mb-2">
                <v-card-text>
                    <div class="field-label">{{txc.acct_conv_acct_type}}</div>
                    <div class="field-value">{{accountTypeName}}</div>
                    <div v-if="isHomeMarket">
                        <div
                            v-if="isShowingConvertButton">
                            <div
                                v-if="accountType == 'CUST' || accountType == 'PREF'">
                                <p class="mb-2">{{txc.convert_account_tagline}}</p>
                                <button
                                    class="primary-solid fluid convert-button"
                                    @click="accountManagement()">
                                    {{txc.convert_account_button_text}}
                                </button>
                            </div>
                            <div v-else>
                                <p>{{txc.acct_conv_tag_line}}</p>
                                <button
                                    class="primary-solid fluid convert-button"
                                    @click="accountManagement(true)">
                                    {{txc.acct_conv_manage}}
                                </button>
                            </div>
                            <p class="mb-2">{{txc.convert_account_desc}}</p>
                        </div>
                    </div>
                </v-card-text>
            </v-card>
        </v-layout>
        <v-layout column>
            <h5 class="cardSectionTitle">{{ txc.card_title_personal_information }}</h5>
            <v-card flat class="mb-2">
                <v-card-text>
                    <section>
                        <div class="field-label">{{ sharedStrings.person.name }}</div>
                        <div class="field-value" v-if="account && account.names">{{ account.names.local }}</div>
                    </section>
                    <section>
                        <div class="field-label">{{ txc.preferred_name }}</div>
                        <div 
                            class="field-value" 
                            data-test-id="personalInfo-preferredName-value">
                            {{ preferredName }}
                        </div>
                        <div class="field-edit">
                            <a href="#" @click.stop="preferredNameDialog = true">{{ txc.edit_preferred_name }}</a>
                        </div>
                        <preferred-name-dialog
                            v-if="preferredNameDialog"
                            :currentPreferredName="preferredName"
                            :fullscreen="fullScreenModals"
                            @closeDialog="preferredNameDialog = false"
                        />
                    </section>
                </v-card-text>
            </v-card>
            <v-card flat class="mb-2">
                <v-card-text>
                    <section>
                        <div class="field-label">{{ txc.address_account_card_title }}</div>
                        <div class="field-label grey--text">{{ txc.address_account_card_description }}</div>
                        <div v-if="marketLoggedIn == 'JP' && isHomeMarket" class="field-value">
                            {{ addressLocal.postalCode }}
                            <span 
                                v-if="japanStates.find(o => o.value == addressLocal.region)">
                                {{ japanStates.find(o => o.value == addressLocal.region).name }}
                            </span>
                            <br />
                            {{ addressLocal.city }}
                            <br />
                            {{ addressLocal.district }}
                            <br />
                            {{ addressLocal.line1 }}
                        </div>
                        <div v-else class="field-value">
                            {{ addressLocal.line1 }}
                            <br />
                            <span v-if="addressLocal.line2">
                                {{ addressLocal.line2 }}
                                <br />
                            </span>
                            <span v-if="addressLocal.city">{{ addressLocal.city }},</span> {{ addressLocal.region }}
                            {{ addressLocal.postalCode }}
                            <br />
                            {{ addressLocal.country }}
                        </div>
                        <div
                            class="field-edit"
                            v-if="marketConfigBooleanByName('enableEditAccountAddress') && isHomeMarket"
                        >
                            <a href="#" @click="openAddressDialog">{{ sharedStrings.address.edit_address }}</a>
                        </div>
                        <ns-modal
                            v-model="addressDialog"
                            :fullscreen="fullScreenModals"
                            scrollable
                            :title="sharedStrings.address.edit_address"
                            @input="closeAddressDialog"
                        >
                            <NsAddressForm
                                ref="nsAddressForm"
                                v-if="Object.keys(modelObject).length"
                                :value="modelObject"
                                :lang="language"
                                :country="country"
                                :include-email="includeEmailInAddressEditForm"
                                :enable-address-suggest="enableAddressSuggest"
                                @input="updateAddress"
                                @address-suggest="openAddressSuggest"
                            />
                        </ns-modal>
                    </section>
                </v-card-text>
            </v-card>
            <v-card flat class="mb-2">
                <v-card-text>
                    <section>
                        <div class="field-label">{{ sharedStrings.person.gender }}</div>
                        <div class="field-value">{{ showGenderLabel }}</div>
                        <div class="field-edit">
                            <a 
                                href="#"
                                @click.stop="genderDialog = true" 
                                v-if="account.country!='JP' && account.country!='SG'">
                                {{ txc.edit_gender }}
                            </a>
                        </div>
                        <gender-dialog
                            v-if="genderDialog"
                            :fullscreen="fullScreenModals"
                            :gender="gender"
                            @closeDialog="genderDialog = false"
                        />
                    </section>
                </v-card-text>
            </v-card>
            <v-card flat class="mb-2">
                <v-card-text>
                    <div class="field-label">{{ sharedStrings.person.date_of_birth }}</div>
                    <div class="field-value">{{ formattedBirthDate }}</div>
                    <div class="field-edit">
                        <a
                            href="#"
                            v-if="!(!!account.persons && !!account.persons[0].birthDate)"
                            @click.stop="openDOBDialog"
                        >
                            {{ txc.add_date_of_birth }}
                        </a>
                    </div>
                    <ns-modal
                        v-model="DOBDialog"
                        :fullscreen="fullScreenModals"
                        :title="txc.add_date_of_birth"
                        @input="closeDOBDialog"
                    >
                        <server-error v-if="!!dialogError" :error="dialogError" />
                        <v-form @submit.prevent>
                            {{ sharedStrings.person.date_of_birth }}
                            <v-menu
                                ref="menu"
                                v-model="menu"
                                :close-on-content-click="false"
                                transition="scale-transition"
                                offset-y
                                full-width
                                min-width="290px"
                            >
                                <template v-slot:activator="{ on }">
                                    <v-text-field :value="formattedBirthDate" readonly v-on="on"/>
                                </template>
                                <v-date-picker
                                    ref="picker"
                                    reactive="true"
                                    v-model="birthDate"
                                    :max="getStartDate"
                                    min="1900-01-01"
                                    @change.native="save"
                                >
                                    <template slot-scope="{ save }">
                                        <v-card-actions>
                                            <v-spacer/>
                                            <button
                                                flat
                                                class="primary-hit fluid"
                                                @click="save;
                                                        menu = false;"
                                            >{{ sharedStrings.generic.ok }}
                                            </button>
                                        </v-card-actions>
                                    </template>
                                </v-date-picker>
                            </v-menu>
                        </v-form>
                        <template v-slot:actions>
                            <v-layout column>
                                <v-flex xs12>
                                    <button
                                        flat
                                        block
                                        class="primary-hit mb-2 fluid"
                                        @click="AddDateOfBirth()"
                                    >{{ sharedStrings.generic.save }}</button>
                                </v-flex>
                                <v-spacer/>
                                <v-flex xs12>
                                    <button
                                        flat
                                        block
                                        outline
                                        class="secondary-hit fluid"
                                        @click="closeDOBDialog()"
                                    >{{ sharedStrings.generic.cancel }}</button>
                                </v-flex>
                            </v-layout>
                        </template>
                    </ns-modal>
                </v-card-text>
            </v-card>
            <v-card flat class="mb-2" ref="nationalitySection" v-if="isInSG">
                <v-card-text>
                    <div class="field-label">{{ sharedStrings.person.nationality }}</div>
                    <div class="field-value">{{ nationality }}</div>
                </v-card-text>
            </v-card>
            <div data-testid="huTaxSection" ref="huTaxSection" v-if="isInHU">
                <h5 class="cardSectionTitle">{{ txc.hu_tax_info.card_title_hu_tax }}</h5>
                <v-card flat class="mb-2">
                    <v-card-text>
                        <section>
                            <p>{{ txc.hu_tax_info.hu_tax_description }}</p>
                            <br />
                            <a :href="huPdfUrl">{{ txc.hu_tax_info.card_title_hu_tax }} PDF</a>
                        </section>
                        <div v-if="huTaxInfoEntered">
                            <section>
                                <div class="field-label"> {{ txc.hu_tax_info.hu_tax_login }}</div>
                                <div class="field-value">{{ huTaxInfo.LOGIN }}</div>
                            </section>
                            <section>
                                <div class="field-label"> {{ txc.hu_tax_info.hu_tax_password }}</div>
                                <div class="field-value">{{ huTaxInfo.PASS }}</div>
                            </section>
                            <section>
                                <div class="field-label"> {{ txc.hu_tax_info.hu_tax_xml_signature }}</div>
                                <div class="field-value">{{ huTaxInfo.XML_SIG }}</div>
                            </section>
                            <section>
                                <div class="field-label"> {{ txc.hu_tax_info.hu_tax_xml_exchange }}</div>
                                <div class="field-value">{{ huTaxInfo.XML_EXCH }}</div>
                            </section>
                        </div>
                        <div v-else>
                            <section>
                                <p>{{ txc.hu_tax_info.hu_tax_not_entered }}</p>
                            </section>
                        </div>
                        <div class="field-edit">
                            <a
                                href="#"
                                @click.stop="openHGTaxDialog"
                            >
                                {{ txc.hu_tax_info.hu_edit_tax_info }}
                            </a>
                        </div>
                        <edit-h-u-tax-info-dialog
                            v-if="HUTaxDialog"
                            :fullscreen="fullScreenModals"
                            @closeDialog="HUTaxDialog = false"
                        />
                    </v-card-text>
                </v-card>
            </div>
        </v-layout>
        <v-layout>
            <v-dialog v-model="showGav" content-class="gav-modal" persistent>
                <v-card>
                    <Gav
                        ref="gav"
                        :locale-country="country"
                        :locale-language="language"
                        :input-address="gavInputAddress"
                        callingModule="defaultCallingModule"
                        @confirm-address="gavConfirmAddress"
                        @toggle-gav="cancelGavPopup"
                    />
                </v-card>
            </v-dialog>
        </v-layout>
    </v-container>
</template>

<script>
import axios from 'axios';
import moment from 'moment';
import {setTimeout} from 'timers';
import { UsesFeatureFlags } from '@nuskin/ns-feature-flags-vue'

import VDragdropSmall from './VDragdropSmall';
import PreferredNameDialog from './forms/PreferredNameDialog';
import EditPasswordDialog from './forms/EditPasswordDialog';
import EditUsernameEmailDialog from './forms/EditUsernameEmailDialog'
import EditHUTaxInfoDialog from './forms/EditHUTaxInfoDialog';
import GenderDialog from './forms/GenderDialog';
import {NsModal} from '@nuskin/design-components';
import ServerError from './ServerError.vue';
// import Stack from '../helpers/contentStackService';
import { NsIcon } from '@nuskin/ns-icon'
import {UserService, ProfileService} from '@nuskin/ns-account';
import {util, dateFormatter, RunConfigService} from '@nuskin/ns-util';

import * as ruleSet from '../repositories/globalRules.js';

import profileUtils from '../repositories/profileUtils.js';
import {mapGetters, mapActions} from 'vuex';
import personalinfohelper from '../helpers/components/personalinfo';
import { NsAddressForm } from '@nuskin/smart-common-components';
import Gav from '@nuskin/smart-common-components/src/components/molecules/forms/Gav/Gav.vue';
import GAV_MIXIN from '@nuskin/smart-common-components/src/mixins/gavMixin.js';
import {getConfiguration, getCachedConfigField} from '@nuskin/configuration-sdk';
import Loader from '../components/Loader';

window.dataObj = {};

export default {
    name: 'PersonalInfo',
    components: {
        VDragdropSmall,
        EditPasswordDialog,
        EditHUTaxInfoDialog,
        PreferredNameDialog,
        EditUsernameEmailDialog,
        GenderDialog,
        NsModal,
        NsIcon,
        ServerError,
        NsAddressForm,
        Gav,
        Loader
    },
    mixins: [UsesFeatureFlags, GAV_MIXIN],
    data() {
        return {
            ruleSet,
            prefNameForm: true,
            user: [],
            usernameDialog: false,
            editPasswordDialog: false,
            preferredNameDialog: false,
            genderDialog: false,
            DOBDialog: false,
            profileDialog: false, 
            HUTaxDialog: false,
            profileImg: {},
            dateOfBirthMask: '##-##-####',
            birthDate: '',
            profileShortKey: '',
            schema: {},
            schemaStr: '{}',
            schemaValid: true,
            modelObject: {},
            modelResponse: {},
            modelStr: '{}',
            modelValid: true,
            formModeEdit: true,
            addressFormValid: false,
            addressDialog: false,
            isUploadFailed: false,
            window,
            date: new Date(),
            dialogError: null,
            menu: false,
            currentCriteria: false,
            japanStates: [],
            origin: '',
            sponsorID: '',
            includeEmailInAddressEditForm: false,
            gavInputAddress: '',
            enableAddressSuggest: false,
            isLoading: false
        };
    },
    computed: {
        ...mapGetters({
            fullScreenModals: 'mobileView',
            txc: 'translationsPersonal',
            sharedStrings: 'sharedStrings',
            preferredName: 'preferredName',
            account: 'account',
            addressLocal: 'localAddress',
            gender: 'gender',
            nationality: 'nationality',
            email: 'email',
            profilePic: 'profilePic',
            isHomeMarket: 'isHomeMarket',
            marketLoggedIn: 'marketLoggedIn',
            languageLoggedIn: 'languageLoggedIn',
            marketConfig: 'marketConfig',
            siteConfig: 'siteConfig',
            marketConfigBooleanByName: 'marketConfigBooleanByName',
            accountType: 'accountType',
            accountTypeName: 'accountTypeName',
            huTaxInfo: 'huTaxInfo',
            isEmailVerified: 'isEmailVerified',
            getUsername: 'getUsername',
            sponsorId: 'sponsorId'
        }),
        isShowingConvertButton(){
            let show = false;
            let sponsor = this.sponsorId
            switch (this.account.beType) {
                case 'DIST':
                    show = this.marketConfigBooleanByName('enableBAConversion');
                    break;
                case 'PREF':
                    show = this.marketConfigBooleanByName('enablePrefConversion')
                        && (sponsor !== 'RETFLOAT') && (sponsor !== 'LEAD') && (sponsor !== 'NSHOUSE');
                    break;
                case 'CUST':
                    show = this.marketConfigBooleanByName('enableCustConversion')
                        && (sponsor !== 'RETFLOAT') && (sponsor !== 'LEAD') && (sponsor !== 'NSHOUSE');
                    break;
            }
            return show
        },
        genderMap() {
            return {
                MALE: this.sharedStrings.person.male,
                FEMALE: this.sharedStrings.person.female,
                OTHER: this.sharedStrings.person.otherGender
            };
        },
        huPdfUrl() {
            let baseUrl = '';
            if(util.RunConfigService.getEnvironmentCode() === 'prod') 
                baseUrl = 'https://www.nuskin.com/content/dam/account/HU/';
            else baseUrl = 'https://test.nuskin.com/content/dam/account/HU/';
            if (this.languageLoggedIn === 'hu') return baseUrl + 'hu_HUTaxDescription.pdf';
            else return baseUrl + 'en_HUTaxDescription.pdf';
        },
        isInHU() {
            return (profileUtils.getCountry() == 'HU' && this.account.country == 'HU' && this.account.beType == 'DIST');
        },
        getSponsor() {
            return this.$store.state.sponsorId;
        },
        isInSG() {
            return (profileUtils.getCountry() == 'SG' && this.account.country == 'SG' && this.account.beType == 'DIST');
        },
        huTaxInfoEntered() {
            if (!this.huTaxInfo.LOGIN && !this.huTaxInfo.PASS && !this.huTaxInfo.XML_SIG && !this.huTaxInfo.XML_EXCH) 
                return false;
            return true;
        },
        showGenderLabel() {
            return this.genderMap[this.gender];
        },
        getStartDate() {
            // return string format "YYYY-MM-DD"
            switch (this.account.beType) {
                case 'DIST':
                    return moment()
                        .subtract(this.marketConfig.baMinAge, 'years')
                        .format(moment.HTML5_FMT.DATE);
                case 'CUST':
                    return moment()
                        .subtract(this.marketConfig.customerMinAge, 'years')
                        .format(moment.HTML5_FMT.DATE);
                case 'PREF':
                default:
                    return moment()
                        .subtract(this.marketConfig.memberMinAge, 'years')
                        .format(moment.HTML5_FMT.DATE);
            }
        },
        formattedBirthDate() {
            if (!this.birthDate && !!this.account.persons && this.account.persons[0].birthDate) {
                const accountData = JSON.parse(localStorage.getItem('nuskin.account.data')) || {}
                localStorage.setItem('nuskin.account.data', JSON.stringify({
                    ...accountData,
                    user: {
                        ...accountData.user,
                        birthDate: this.account.persons[0].birthDate
                    }
                }));
                return dateFormatter.getCountryFormattedDate(
                    this.account.persons[0].birthDate, profileUtils.getLanguage() + '_' + profileUtils.getCountry()
                );
            }
            let date = this.birthDate;
            return date
                ? moment(date).format(this.marketConfig.addBirthdateMask)
                : '';
        },
        showEmailVerify() {
            return Boolean(this.featureEnabled('cx13-15592-enable-email-verify-feature'))
        },
        language() {
            const runConfig = RunConfigService.getRunConfig();
            return runConfig?.language;
        },
        country() {
            const runConfig = RunConfigService.getRunConfig();
            return runConfig?.country;
        }
    },
    methods: {
        ...mapActions([
            'updateProfilePic',
            'setAccount',
            'setEmailCardShowing',
            'updateLocalAddress',
            'setJapanStates',
            'updateSigKey'
        ]),

        accountManagement(isLegacyAcctConversion = false) {
            personalinfohelper.accountManagement(this,profileUtils);
            window.location.href = this.getUpgradeUrl(isLegacyAcctConversion);
        },

        getUpgradeUrl(isLegacyAcctConversion) {
            if (isLegacyAcctConversion) return './profile/convert-account.html';
            const { origin, sponsorId } = this.getUpgradeUrlParams();
            if (!origin || !sponsorId) return;
            const isProd = RunConfigService.getEnvironmentCode() !== 'test';
            const domain = isProd ? 'www.nuskin.com' : 'test.nuskin.com';
            const runConfig = RunConfigService.getRunConfig();
            const locale = `${runConfig?.language}_${runConfig?.country}`;
            // eslint-disable-next-line max-len
            const builtUrl = `https://${domain}/static/signup/${locale}/BRAND_AFFILIATE/signup-page/?origin=${origin}&sponsor=${sponsorId}&conversion=true`;
            return builtUrl;
        },

        getUpgradeUrlParams() {
            const origin = 'WBV';
            const sponsorId = this.sponsorId;
            return { origin, sponsorId }
        },

        toggleLoader(showFlag) {
            personalinfohelper.toggleLoader(showFlag);
        },

        /**
             * Username
             */
        async handleCloseDialog() {
            this.usernameDialog = false;
        },
        /**
             * Profile Image
             */
        updateProfileImageData(blob) {
            personalinfohelper.updateProfileImageData(this,blob);
        },
        imageIsUpdated() {
            personalinfohelper.imageIsUpdated(this,UserService,profileUtils);
        },
        uploadImage() {

            let formData = new FormData();
            let filename = 'profile_' + Date.now() + '.jpg';
            personalinfohelper.uploadImage(formData,filename,this,axios,this.marketConfig);
        },
        openProfileDialog() {
            personalinfohelper.openProfileDialog(profileUtils,this);
        },
        closeProfileDialog() {
            personalinfohelper.closeProfileDialog(profileUtils,this);
        },

        /**
             * DateOfBirth
             */
        openDOBDialog() {
            personalinfohelper.openDOBDialog(profileUtils,this);
        },
        closeDOBDialog() {
            personalinfohelper.closeDOBDialog(profileUtils,this);
        },
        AddDateOfBirth() {
            personalinfohelper.AddDateOfBirth(this,ProfileService,profileUtils);
        },

        /**
             * Address Info
             */
        closeAddressDialog() {
            personalinfohelper.closeAddressDialog(this,profileUtils);
            this.showLoader(false);
        },
        openAddressDialog() {
            personalinfohelper.openAddressDialog(this,profileUtils);
        },
        updateAddress(address) {
            const updatedAddress = { ...address };
            if (address['address1']) updatedAddress.line1 = address.address1;
            if (address['address2']) updatedAddress.line2 = address.address2;
            this.modelResponse = updatedAddress;
            personalinfohelper.updateAddress(this,profileUtils,ProfileService);
        },
        dataUpdate(data) {
            personalinfohelper.dataUpdate(this, data);
        },
        /**
         *  HU Tax
         */
        openHGTaxDialog() {
            personalinfohelper.openHGTaxDialog(this, profileUtils);
        },
        closeHGTaxDialog() {
            personalinfohelper.closeHGTaxDialog(this, profileUtils);
        },

        // gav - address suggest functionality
        async gavConfirmAddress(address) {
            const [modified] = this.handleGavConfirm(address);
            if (modified) {
                this.$refs.nsAddressForm.saveAddress();
            }
        },

        cancelGavPopup() {
            this.cancelGav();
            this.showLoader(false);
        },

        openAddressSuggest(inputAddress) {
            this.showLoader(true);
            this.gavInputAddress = inputAddress;
            this.openGavAndLoadSuggestions(this.gavInputAddress, true);
        },

        showLoader(loading) {
            this.isLoading = false;
            this.isLoading = loading;
        },

        getMarketConfig() {
            const isAddressValidationEnabled = getCachedConfigField('enableAddressValidation');
            this.enableAddressSuggest = isAddressValidationEnabled;
        },

        async initMarketConfig() {
            await getConfiguration(['Checkout', 'Account', 'Basic']);
            this.getMarketConfig();
        }
    },
    watch: {
        menu(val) {
            val && setTimeout(() => (this.$refs.picker.activePicker = 'YEAR'));
        },
        huTaxInfo(val) {
            if (!val.XML_SIG.includes('-') && val.XML_SIG.length === 30) {
                this.updateSigKey(
                    val.XML_SIG.substr(0,2) + '-' + val.XML_SIG.substr(2,4) + '-' + val.XML_SIG.substr(6)
                );
            }
        }
    },
    mounted() {
        this.initMarketConfig();
    }
};
</script>

<style lang="scss">
@use "sass:map";
@use "~@nuskin/ns-core-styles/src/scss/colors";
@use "~@nuskin/ns-core-styles/src/scss/typographyVariables";
@use "~@nuskin/ns-core-styles/src/scss/gridStyles";
@use "~@nuskin/ns-core-styles/src/scss/vuetifyOverrides";
@use "~@nuskin/ns-core-styles/src/scss/global.mixins";

$dryink: map.get(colors.$baseColors, "dryink");
$blackhole: map.get(colors.$baseColors, "blackhole");
$eyore: map.get(colors.$baseColors, "eyore");

.container {
  .v-input--selection-controls {
    padding-top: 0;
    margin-top: 10px !important;
  }

  .v-menu__content.menuable__content__active.pop-menu {
    min-width: 300px !important;
    border: 3px solid $dryink !important;
    overflow: hidden;
  }

  .v-menu__content:not(.v-menu__content) > .v-picker {
    z-index: 101 !important;
  }

  .v-input--selection-controls:not(.v-input--hide-details) .v-input__slot {
    align-items: center;

    label {
      margin-bottom: 0;

      ul {
        padding-left: 0;
      }

      li {
        list-style: none;
      }
    }
  }

  .v-select .v-input__control .v-input__slot {
    border: none;
    padding: 0;

    .v-select__slot {
      flex-flow: column nowrap !important;

      .v-input__append-inner {
        bottom: 7px;
        right: 5px;
        top: auto;
        margin: 0;
        z-index: 100;
        position: absolute;
      }
      .v-select__selections {
        position: relative;

        .v-select__selection--comma {
          margin: 0;
          position: absolute;
          top: 50%;
          left: 20px;
          transform: translateY(-50%);
          z-index: 100;
        }

        input {
          border: 1px solid $dryink;

          &:focus {
            box-shadow: 0 0 5px 2px rgba(113, 35, 217, 0.2);
          }

          &:disabled {
              border-color: rgba(0, 0, 0, 0.38);
          }
        }
      }
      label.primary--text {
        color: $blackhole !important;
        font-size: 16px;
        font-family: "proxima-nova", sans-serif;
      }
      svg {
        color: $blackhole !important;
      }
    }
  }

  .v-label {
    font-family: map.get(typographyVariables.$typography, "font-family-primary");
  }

  .v-radio {
    justify-content: flex-start;

    .v-icon.accent--text {
      color: map.get(colors.$baseColors, "shells") !important;
    }
  }

  .v-radio--is-disabled {
    .v-label {
      color: $eyore;
    }
  }

  .v-input--radio-group {
    .v-input__control {
      width: 100%;
    }
  }

  .v-input--radio-group {
    .v-input__control {
      width: 100%;
    }
  }

  .radio-option {
    display: flex;
    justify-content: flex-start;
    cursor: pointer;

    .label {
      margin-left: -22px;
    }
  }
}
</style>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped lang="scss">
h3 {
  margin: 40px 0 0;
}

ul {
  list-style-type: none;
  padding: 0;
}

li {
  display: inline-block;
  margin: 0 10px;
}

#loader {
    z-index: 9999;
    position: absolute;
    top: auto;
    left: auto;
}

.field-append-label {
  position: absolute;
  right: 6px !important;
  font-size: 13px;
  text-transform: uppercase;
  font-weight: 600;
  top: 3px;
  cursor: pointer;
}

.convert-button {
    margin-top: 1em;
    margin-bottom: .5em;
}

.verified-container {
  display: flex;
  flex-flow: row nowrap;
  justify-content: space-between;
  align-items: center;
  .right-side {
    margin-left: auto;
    font-size: 13px;
    font-weight: 400;
    line-height: 18px;
    .badge-container {
      padding: 3px 5px;
      display: flex;
      justify-content: space-between;
      flex-flow: row nowrap;
      align-items: center;
      .icon {
        margin-right: 5px;
        height: 16px;
        width: 16px;
      }
      &.success {
        background: #DEEEE7;
        color: #5BAC85;
      }
      &.danger {
        background: #f4d8d8;
        color: #c81d1d;
      }
    }
  }
}
</style>